import type { User, UserManager } from 'oidc-client-ts';
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import { AuthContext } from './auth-context';
import { createUserManager } from './config';
import type {
  AuthConfig,
  AuthContextType,
  AuthState,
  LoginOptions,
  ZitadelUserProfile,
} from './types';
import { extractRoles, extractSlugs } from './utils';

export interface AuthProviderProps {
  children: ReactNode;
  /** Auth configuration for Zitadel */
  config: AuthConfig;
}

/**
 * Authentication Provider Component
 *
 * Wraps the application and provides authentication state and methods.
 * Uses Zitadel OIDC for authentication.
 *
 * @example
 * ```tsx
 * const authConfig = {
 *   authority: process.env.VITE_ZITADEL_ISSUER,
 *   clientId: process.env.VITE_ZITADEL_CLIENT_ID,
 *   appUrl: process.env.VITE_APP_URL,
 * };
 *
 * <AuthProvider config={authConfig}>
 *   <App />
 * </AuthProvider>
 * ```
 */
export function AuthProvider({ children, config }: AuthProviderProps) {
  const [state, setState] = useState<AuthState>({
    isLoading: true,
    isAuthenticated: false,
    user: null,
    profile: null,
    accessToken: null,
    error: null,
    roles: [],
  });

  // Create UserManager lazily (only in browser)
  const userManager = useMemo<UserManager | null>(() => {
    if (typeof window === 'undefined') {
      return null;
    }
    return createUserManager(config);
  }, [config]);

  /**
   * Initialize auth state on mount
   */
  useEffect(() => {
    if (!userManager) return;

    async function initAuth() {
      try {
        const user = await userManager!.getUser();

        if (user && !user.expired) {
          const profile = user.profile as ZitadelUserProfile;
          setState({
            isLoading: false,
            isAuthenticated: true,
            user,
            profile,
            accessToken: user.access_token,
            error: null,
            roles: extractRoles(profile),
          });
        } else {
          setState({
            isLoading: false,
            isAuthenticated: false,
            user: null,
            profile: null,
            accessToken: null,
            error: null,
            roles: [],
          });
        }
      } catch (error) {
        console.error('[Auth] Failed to initialize:', error);
        setState({
          isLoading: false,
          isAuthenticated: false,
          user: null,
          profile: null,
          accessToken: null,
          error: error instanceof Error ? error : new Error(String(error)),
          roles: [],
        });
      }
    }

    initAuth();
  }, [userManager]);

  /**
   * Subscribe to user manager events
   */
  useEffect(() => {
    if (!userManager) return;

    const handleUserLoaded = (user: User) => {
      const profile = user.profile as ZitadelUserProfile;
      setState((prev) => ({
        ...prev,
        isAuthenticated: true,
        user,
        profile,
        accessToken: user.access_token,
        error: null,
        roles: extractRoles(profile),
      }));
    };

    const handleUserUnloaded = () => {
      setState((prev) => ({
        ...prev,
        isAuthenticated: false,
        user: null,
        profile: null,
        accessToken: null,
        roles: [],
      }));
    };

    const handleSilentRenewError = (error: Error) => {
      console.error('[Auth] Silent renew failed:', error);
      // Don't update error state for silent renew failures
      // The token might still be valid
    };

    userManager.events.addUserLoaded(handleUserLoaded);
    userManager.events.addUserUnloaded(handleUserUnloaded);
    userManager.events.addSilentRenewError(handleSilentRenewError);

    return () => {
      userManager.events.removeUserLoaded(handleUserLoaded);
      userManager.events.removeUserUnloaded(handleUserUnloaded);
      userManager.events.removeSilentRenewError(handleSilentRenewError);
    };
  }, [userManager]);

  /**
   * Login - redirect to Zitadel
   */
  const login = useCallback(
    async (options?: LoginOptions) => {
      if (!userManager) return;

      try {
        // Build extra params for organization-scoped login
        const extraQueryParams: Record<string, string> = {};
        if (options?.organizationId) {
          // Zitadel supports org-scoped login via scope or query param
          extraQueryParams['organization'] = options.organizationId;
        }

        // Store return URL for redirect after callback
        if (options?.returnTo) {
          sessionStorage.setItem('auth_return_to', options.returnTo);
        }

        await userManager.signinRedirect({
          extraQueryParams:
            Object.keys(extraQueryParams).length > 0
              ? extraQueryParams
              : undefined,
        });
      } catch (error) {
        console.error('[Auth] Login failed:', error);
        setState((prev) => ({
          ...prev,
          error: error instanceof Error ? error : new Error(String(error)),
        }));
      }
    },
    [userManager],
  );

  /**
   * Logout - end Zitadel session via OIDC signoutRedirect
   *
   * Uses signoutRedirect() to navigate to Zitadel's end_session endpoint,
   * which terminates the server-side session (preventing auto-re-login).
   * The OIDC config has revokeTokensOnSignout=true so tokens are revoked
   * automatically. After session ends, Zitadel redirects back to
   * post_logout_redirect_uri (config.appUrl).
   */
  const logout = useCallback(async () => {
    if (!userManager) return;

    try {
      await userManager.signoutRedirect();
    } catch (error) {
      console.error('[Auth] Logout redirect failed:', error);
      // Fallback: clear local state so ProtectedRoute redirects to login
      await userManager.removeUser().catch(() => {});
      setState({
        isLoading: false,
        isAuthenticated: false,
        user: null,
        profile: null,
        accessToken: null,
        error: null,
        roles: [],
      });
    }
  }, [userManager]);

  /**
   * Get access token (refreshes if needed)
   */
  const getAccessToken = useCallback(async (): Promise<string | null> => {
    if (!userManager) return null;

    try {
      let user = await userManager.getUser();

      if (!user) {
        return null;
      }

      // Check if token is expired or about to expire
      if (user.expired) {
        // Try silent renew
        user = await userManager.signinSilent();
        if (!user) {
          return null;
        }
      }

      return user.access_token;
    } catch (error) {
      console.error('[Auth] Failed to get access token:', error);
      return null;
    }
  }, [userManager]);

  // Extract tenant/org slugs from profile
  const { tenantSlug, organizationSlug } = useMemo(
    () => extractSlugs(state.profile),
    [state.profile],
  );

  // Role checking helpers
  const hasRole = useCallback(
    (role: string): boolean => {
      return state.roles.includes(role);
    },
    [state.roles],
  );

  const hasAnyRole = useCallback(
    (roles: string[]): boolean => {
      return roles.some((role) => state.roles.includes(role));
    },
    [state.roles],
  );

  const contextValue: AuthContextType = useMemo(
    () => ({
      ...state,
      login,
      logout,
      getAccessToken,
      tenantSlug,
      organizationSlug,
      hasRole,
      hasAnyRole,
    }),
    [
      state,
      login,
      logout,
      getAccessToken,
      tenantSlug,
      organizationSlug,
      hasRole,
      hasAnyRole,
    ],
  );

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
}
